import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Trash2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { sendChatMessage, getChatHistory, clearChatHistory } from '../chatApi';
import { toast } from '../hooks/use-toast';

const ChatPanel = ({ worker }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const scrollRef = useRef(null);

  const load = async () => {
    setLoadingHistory(true);
    try {
      const msgs = await getChatHistory(worker.id);
      setMessages(msgs);
    } catch {
      // silent fail on history load
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (worker?.id) load();
     
  }, [worker?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    // optimistic
    const optimisticUser = { role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimisticUser]);
    try {
      const res = await sendChatMessage(worker.id, text);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.reply, created_at: new Date().toISOString() },
      ]);
    } catch (err) {
      toast({
        title: 'Chat error',
        description: err?.response?.data?.detail || 'Please try again.',
      });
      // rollback optimistic
      setMessages((prev) => prev.filter((m) => m !== optimisticUser));
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear this conversation?')) return;
    try {
      await clearChatHistory(worker.id);
      setMessages([]);
      toast({ title: 'Conversation cleared' });
    } catch {
      toast({ title: 'Could not clear', description: 'Please try again.' });
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-200 bg-white px-5 py-3">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br ${worker.avatarColor}`}>
            {worker.avatar ? (
              <img src={worker.avatar} alt={worker.name} className="h-full w-full object-cover" />
            ) : (
              <span className="font-semibold text-neutral-800">{worker.initial}</span>
            )}
          </div>
          <div>
            <p className="font-semibold leading-tight text-neutral-900">{worker.name}</p>
            <p className="text-xs text-neutral-500">{worker.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={load}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800"
            aria-label="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={handleClear}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-red-50 hover:text-red-600"
            aria-label="Clear conversation"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-neutral-50 px-5 py-6">
        {loadingHistory ? (
          <div className="flex justify-center py-8 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="mx-auto max-w-md pt-10 text-center">
            <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br ${worker.avatarColor}`}>
              {worker.avatar ? (
                <img src={worker.avatar} alt={worker.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-2xl font-semibold text-neutral-800">{worker.initial}</span>
              )}
            </div>
            <h3 className="font-serif text-2xl tracking-tight text-neutral-900">Say hi to {worker.name}</h3>
            <p className="mt-2 text-sm text-neutral-600">
              {worker.name} is your AI {worker.role}. Ask them to take a task, ask for suggestions, or just say hi.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={
                    'max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed ' +
                    (m.role === 'user'
                      ? 'bg-neutral-900 text-white'
                      : 'border border-neutral-200 bg-white text-neutral-800')
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-4 py-2.5 text-neutral-500">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400" />
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400" style={{ animationDelay: '150ms' }} />
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={handleSend} className="border-t border-neutral-200 bg-white px-4 py-3">
        <div className="flex items-end gap-2 rounded-xl border border-neutral-200 bg-white p-2 focus-within:border-neutral-400">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={`Message ${worker.name}…`}
            rows={1}
            disabled={sending}
            className="max-h-32 flex-1 resize-none rounded-lg bg-transparent px-2 py-1.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none disabled:opacity-60"
          />
          <Button
            type="submit"
            size="icon"
            disabled={sending || !input.trim()}
            className="h-9 w-9 shrink-0 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <p className="mt-2 px-1 text-[11px] text-neutral-400">
          Press Enter to send · Shift + Enter for new line · AI outputs may contain errors.
        </p>
      </form>
    </div>
  );
};

export default ChatPanel;
